import React, { useCallback } from 'react';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { generateDiscoveryQuestions, getDiscoveryJobStatus, getDiscoveryReportByJob } from '../services/api';
import { useJobReport } from '../hooks/useJobReport';
import { DiscoveryReportView } from './discovery/DiscoveryReportView';
import { ReportEmpty, ReportGenerating, ReportLoading, RegenerateButton, RegeneratingNote } from './report/ReportStates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MESSAGES = [
  'Analyzing role requirements and seniority level...',
  'Role clarity and day-to-day reality...',
  'Technical and skill requirements...',
  'Team dynamics and culture...',
  'Success metrics and growth...',
  'Red flags and deal-breakers...',
  'Compensation, interview process and logistics...',
  'Building your must-confirm checklist...',
];

const DiscoveryQuestionsTab = ({ jobId, jobTitle, jobDescription, companyName, readOnly }) => {
  const start = useCallback(() => {
    if (!jobDescription) {
      toast.error('Job description is required to generate discovery questions');
      return null;
    }
    return generateDiscoveryQuestions({ job_title: jobTitle, job_description: jobDescription, company_name: companyName || '', hirefast_job_id: jobId });
  }, [jobId, jobTitle, jobDescription, companyName]);

  const r = useJobReport({ jobId, fetchByJob: getDiscoveryReportByJob, fetchStatus: getDiscoveryJobStatus, start, name: 'Discovery call guide' });

  if (r.loading) return <ReportLoading />;
  if (!r.data && r.running) return <ReportGenerating Icon={MessageSquare} tone="teal" title="Crafting Discovery Call Questions" messages={MESSAGES} testid="discovery-generating" />;
  if (!r.data) {
    return (
      <ReportEmpty Icon={MessageSquare} tone="teal" title="Discovery Call Questions" failed={r.failed} onGenerate={r.generate} readOnly={readOnly}
        label="Generate Discovery Questions" testid="discovery-empty-state" buttonTestid="discovery-generate-btn"
        text="A structured guide for your call with the hiring manager: 6 sections of strategic questions with why-ask insights, follow-up angles and a must-confirm checklist." />
    );
  }
  return (
    <div className="space-y-4">
      {r.running && <RegeneratingNote testid="discovery-regenerating-note" />}
      <DiscoveryReportView data={r.data.questions} pdfUrl={`${BACKEND_URL}/api/discovery/${r.data.discovery_id}/pdf`}
        actions={!readOnly && <RegenerateButton onClick={r.generate} running={r.running} label="Regenerate Questions" testid="discovery-regenerate-btn" />} />
    </div>
  );
};

export default DiscoveryQuestionsTab;
